/* ThermoAspen Heater Roof Accommodation v1.1.0
 * Stage 2C — canonical vertical twin-wall roof penetration accommodation.
 *
 * Purpose:
 * - consume canonical Building Geometry + resolved Heater Installation;
 * - cut affected rafters on vertical trimmer planes;
 * - add downslope/upslope vertical trimmers;
 * - add up to two short rafters beside the flue when they physically fit;
 * - remove/clip normal roof layers inside the twin-wall combustible-clearance zone.
 *
 * This module does NOT select the heater or route the flue.
 * It consumes the Heater System Engine's resolved installation.
 */
(function(W){
'use strict';

var VERSION='1.1.2';
var COORD='ti-room-xyz-v1';
var EPS=0.01;
var DEFAULT_TWINWALL_OUTER_MM=220;
var METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM=20;
var DEFAULT_COMBUSTIBLE_CLEARANCE_MM=100;

function clone(v){return v==null?v:JSON.parse(JSON.stringify(v))}
function arr(v){return Array.isArray(v)?v:[]}
function num(v,f){v=Number(v);return isFinite(v)?v:(f==null?null:f)}
function issue(a,code,path,message,data){var x={code:code,path:path,message:message};if(data!==undefined)x.data=data;a.push(x)}
function purpose(m){return String(m&&m.assembly&&m.assembly.purpose||m&&m.purpose||'')}
function role(m){return String(m&&m.assembly&&m.assembly.role||m&&m.render&&m.render.role||'')}
function coord(p,axis){return axis==='x'?num(p&&p.x,0):num(p&&p.z,0)}
function setCoord(p,axis,v){if(axis==='x')p.x=v;else p.z=v;return p}
function otherAxis(axis){return axis==='x'?'z':'x'}
function V(x,y,z){return{x:num(x,0),y:num(y,0),z:num(z,0)}}
function add(a,b){return V(a.x+b.x,a.y+b.y,a.z+b.z)}
function sub(a,b){return V(a.x-b.x,a.y-b.y,a.z-b.z)}
function mul(a,k){return V(a.x*k,a.y*k,a.z*k)}
function dot(a,b){return a.x*b.x+a.y*b.y+a.z*b.z}
function cross(a,b){return V(a.y*b.z-a.z*b.y,a.z*b.x-a.x*b.z,a.x*b.y-a.y*b.x)}
function len(a){return Math.sqrt(dot(a,a))}
function unit(a){var L=len(a);return L>1e-9?mul(a,1/L):null}
function axisVec(axis,sign){return axis==='x'?V(sign,0,0):V(0,0,sign)}
function upVec(){return V(0,1,0)}
function localToWorld(fr,p){
  return{
    x:fr.originMm.x+fr.xAxis.x*p.x+fr.yAxis.x*p.y+fr.zAxis.x*p.z,
    y:fr.originMm.y+fr.xAxis.y*p.x+fr.yAxis.y*p.y+fr.zAxis.y*p.z,
    z:fr.originMm.z+fr.xAxis.z*p.x+fr.yAxis.z*p.y+fr.zAxis.z*p.z
  };
}
function worldDirToLocal(fr,v){return{x:dot(v,fr.xAxis),y:dot(v,fr.yAxis),z:dot(v,fr.zAxis)}}
function worldPointToLocal(fr,p){var d=sub(p,fr.originMm);return{x:dot(d,fr.xAxis),y:dot(d,fr.yAxis),z:dot(d,fr.zAxis)}}
function aabbFromPoints(pts){
  if(!pts.length)return null;
  var a={x1:Infinity,x2:-Infinity,y1:Infinity,y2:-Infinity,z1:Infinity,z2:-Infinity};
  pts.forEach(function(p){a.x1=Math.min(a.x1,p.x);a.x2=Math.max(a.x2,p.x);a.y1=Math.min(a.y1,p.y);a.y2=Math.max(a.y2,p.y);a.z1=Math.min(a.z1,p.z);a.z2=Math.max(a.z2,p.z)});
  return{centerMm:{x:(a.x1+a.x2)/2,y:(a.y1+a.y2)/2,z:(a.z1+a.z2)/2},sizeMm:{x:a.x2-a.x1,y:a.y2-a.y1,z:a.z2-a.z1},aabbMm:a};
}
function boxWorld(cx,cy,cz,sx,sy,sz){
  return{centerMm:{x:cx,y:cy,z:cz},sizeMm:{x:sx,y:sy,z:sz},aabbMm:{x1:cx-sx/2,x2:cx+sx/2,y1:cy-sy/2,y2:cy+sy/2,z1:cz-sz/2,z2:cz+sz/2}};
}
function refreshBoxAabb(m){
  var w=m&&m.world;if(!w||!w.centerMm||!w.sizeMm)return;
  var sx=num(w.sizeMm.x,0),sy=num(w.sizeMm.y,0),sz=num(w.sizeMm.z,0),r=m&&m.render&&m.render.rotationRad||{},ax=sx,ay=sy,az=sz;
  if(r.x){var c=Math.abs(Math.cos(r.x)),q=Math.abs(Math.sin(r.x)),ny=ay*c+az*q,nz=ay*q+az*c;ay=ny;az=nz}
  if(r.y){var cy=Math.abs(Math.cos(r.y)),qy=Math.abs(Math.sin(r.y)),nx=ax*cy+az*qy,nzz=ax*qy+az*cy;ax=nx;az=nzz}
  if(r.z){var cz=Math.abs(Math.cos(r.z)),qz=Math.abs(Math.sin(r.z)),nxx=ax*cz+ay*qz,nyy=ax*qz+ay*cz;ax=nxx;ay=nyy}
  var c0=w.centerMm;w.aabbMm={x1:c0.x-ax/2,x2:c0.x+ax/2,y1:c0.y-ay/2,y2:c0.y+ay/2,z1:c0.z-az/2,z2:c0.z+az/2};
}
function prismFaces(N){
  var f=[[],[]],i,j;
  for(i=0;i<N;i++){f[0].push(i);f[1].unshift(N+i)}
  for(i=0;i<N;i++){j=(i+1)%N;f.push([i,j,N+j,N+i])}
  return f;
}
function uniqSorted(a){
  var out=[];
  a.filter(function(x){return isFinite(Number(x))}).map(Number).sort(function(a,b){return a-b}).forEach(function(x){
    if(!out.length||Math.abs(x-out[out.length-1])>.001)out.push(x);
  });
  return out;
}

function flueInfo(installation){
  var f=installation&&installation.assembly&&installation.assembly.flue||{},
      rz=f.reserveZone||{},
      ps=arr(f.penetrations),
      roof=ps.find(function(p){return p&&p.hostType==='roof'}),
      segs=arr(f.segments),
      insulated=segs.find(function(s){return s&&s.type==='insulated-twin-wall'}),
      outer=num(rz.outerDiameterMm,num(insulated&&insulated.geometry&&insulated.geometry.outerDiameterMm,DEFAULT_TWINWALL_OUTER_MM)),
      clearance=num(rz.combustibleClearanceMm,DEFAULT_COMBUSTIBLE_CLEARANCE_MM),
      radius=num(rz.radiusMm,outer/2+clearance),
      p=roof&&roof.centreMm||insulated&&insulated.geometry&&insulated.geometry.startMm||null;
  if(!p)return null;
  return{
    centreMm:clone(p),
    outerDiameterMm:outer,
    outerRadiusMm:outer/2,
    combustibleClearanceMm:clearance,
    exclusionRadiusMm:radius
  };
}

function roofFrame(building,flue){
  var r=building&&building.metrics&&building.metrics.roof||{},family=String(r.family||r.form||''),
      runAxis,crossAxis,upSign,pitch,side=null;
  if(family==='pitched-gable'){
    runAxis=r.spanAxis;
    crossAxis=r.ridgeAxis;
    var ru=coord(flue.centreMm,runAxis),ridge=num(r.ridgeCoordMm,0);
    side=ru<ridge?-1:1;
    upSign=side<0?1:-1;
    pitch=num(r.pitchRad,0);
  }else if(family==='monopitch'){
    runAxis=r.axis;
    crossAxis=otherAxis(runAxis);
    upSign=num(r.sign,1)>=0?1:-1;
    pitch=num(r.pitchRad,0);
  }else return null;
  return{
    family:family,
    runAxis:runAxis,
    crossAxis:crossAxis,
    upSign:upSign,
    side:side,
    pitchRad:pitch,
    flueRunMm:coord(flue.centreMm,runAxis),
    flueCrossMm:coord(flue.centreMm,crossAxis)
  };
}

function roofTopY(building,rf,run){
  var r=building.metrics.roof||{};
  if(rf.family==='pitched-gable'){
    return num(r.theoreticalRafterApexMm,0)-Math.abs(run-num(r.ridgeCoordMm,0))*Math.tan(rf.pitchRad);
  }
  var p0=r.rafterUnderside&&r.rafterUnderside.lowPoint||{},
      u0=num(p0.axisCoordMm,r.lowBearing&&r.lowBearing.uphillMm),
      y0=num(r.rafterTop&&r.rafterTop.lowPointYmm,0),
      rise=num(r.rafterUnderside&&r.rafterUnderside.slopeRisePerRun,Math.tan(rf.pitchRad));
  return y0+(run-u0)*num(r.sign,1)*rise;
}

function isRafterForPlane(m,rf){
  if(purpose(m)!=='rafter')return false;
  if(role(m)==='roof-trimmer'||role(m)==='short-rafter')return false;
  var id=String(m&&m.id||'');
  if(rf.family==='pitched-gable'){
    if(rf.side<0)return /\.negative$/.test(id);
    return /\.positive$/.test(id);
  }
  return true;
}

function rafterDatum(m,rf){
  var fr=m&&m.geometry&&m.geometry.localFrame||null,w=num(m&&m.section&&m.section.widthMm,44);
  if(!fr||!fr.originMm)return null;
  return{id:m.id,member:m,crossMm:coord(fr.originMm,rf.crossAxis),widthMm:w};
}

function buildPlan(building,installation){
  var errors=[],warnings=[],flue=flueInfo(installation),rf=flue&&roofFrame(building,flue);
  if(!building||building.valid===false)issue(errors,'BUILDING_INVALID','building','Canonical Building Geometry is required.');
  if(!flue)issue(errors,'FLUE_GEOMETRY_MISSING','installation.assembly.flue','Resolved vertical twin-wall flue geometry is required.');
  if(!rf)issue(errors,'ROOF_FAMILY_UNSUPPORTED','building.metrics.roof','Only pitched-gable and monopitch roofs are currently supported.');
  if(errors.length)return{valid:false,version:VERSION,errors:errors,warnings:warnings};

  var R=flue.exclusionRadiusMm,rafters=arr(building.structuralMembers).filter(function(m){return isRafterForPlane(m,rf)}).map(function(m){return rafterDatum(m,rf)}).filter(Boolean).sort(function(a,b){return a.crossMm-b.crossMm});
  if(!rafters.length){
    issue(errors,'RAFTERS_NOT_FOUND','building.structuralMembers','No canonical rafters were found on the penetrated roof plane.');
    return{valid:false,version:VERSION,errors:errors,warnings:warnings};
  }

  var affected=rafters.filter(function(q){return Math.max(0,Math.abs(q.crossMm-rf.flueCrossMm)-q.widthMm/2)<R-EPS}),
      unaffected=rafters.filter(function(q){return affected.indexOf(q)<0}),
      lower=unaffected.filter(function(q){return q.crossMm<rf.flueCrossMm}).pop()||null,
      upper=unaffected.filter(function(q){return q.crossMm>rf.flueCrossMm})[0]||null;

  if(!affected.length)
    issue(warnings,'NO_RAFTER_INTERSECTION','roof.rafters','No rafter enters the 100 mm twin-wall clearance zone; no rafter trimming is required.');

  if(affected.length&&(!lower||!upper))
    issue(errors,'TRIMMER_SUPPORT_RAFTER_MISSING','roof.rafters','The flue opening does not have an unaffected supporting rafter on both sides. Edge-framing rules are not yet defined.',
      {lowerSupport:lower&&lower.id||null,upperSupport:upper&&upper.id||null});

  var ref=affected[0]||rafters.reduce(function(best,q){return !best||Math.abs(q.crossMm-rf.flueCrossMm)<Math.abs(best.crossMm-rf.flueCrossMm)?q:best},null),
      rafterWidth=num(ref&&ref.widthMm,44),
      rafterDepth=num(ref&&ref.member&&ref.member.section&&ref.member.section.heightMm,125),
      nearDown=rf.flueRunMm-rf.upSign*R,
      nearUp=rf.flueRunMm+rf.upSign*R,
      farDown=rf.flueRunMm-rf.upSign*(R+rafterWidth),
      farUp=rf.flueRunMm+rf.upSign*(R+rafterWidth),
      lowerInside=lower?lower.crossMm+lower.widthMm/2:null,
      upperInside=upper?upper.crossMm-upper.widthMm/2:null,
      trimmerLength=lower&&upper?upperInside-lowerInside:null;

  var shortRafters=[];
  if(lower&&upper&&affected.length){
    var leftRange={a:rf.flueCrossMm-R-rafterWidth,b:rf.flueCrossMm-R},
        rightRange={a:rf.flueCrossMm+R,b:rf.flueCrossMm+R+rafterWidth},
        leftFits=leftRange.a>=lowerInside-EPS,
        rightFits=rightRange.b<=upperInside+EPS;
    if(leftFits)shortRafters.push({side:'lower-cross',crossCentreMm:(leftRange.a+leftRange.b)/2,cross0Mm:leftRange.a,cross1Mm:leftRange.b});
    else issue(warnings,'SHORT_RAFTER_OMITTED','roof.shortRafters.lower-cross','Insufficient space for the lower-cross short rafter.',{availableMm:(rf.flueCrossMm-R)-lowerInside,requiredMm:rafterWidth});
    if(rightFits)shortRafters.push({side:'upper-cross',crossCentreMm:(rightRange.a+rightRange.b)/2,cross0Mm:rightRange.a,cross1Mm:rightRange.b});
    else issue(warnings,'SHORT_RAFTER_OMITTED','roof.shortRafters.upper-cross','Insufficient space for the upper-cross short rafter.',{availableMm:upperInside-(rf.flueCrossMm+R),requiredMm:rafterWidth});
  }

  return{
    valid:errors.length===0,
    version:VERSION,
    coordinateSystemId:COORD,
    errors:errors,
    warnings:warnings,
    roof:{
      family:rf.family,
      runAxis:rf.runAxis,
      crossAxis:rf.crossAxis,
      upslopePlanSign:rf.upSign,
      side:rf.side,
      pitchRad:rf.pitchRad
    },
    flue:{
      centreMm:clone(flue.centreMm),
      outerDiameterMm:flue.outerDiameterMm,
      combustibleClearanceMm:flue.combustibleClearanceMm,
      exclusionRadiusMm:R,
      exclusionDiameterMm:2*R
    },
    framing:{
      rafterSectionMm:{width:rafterWidth,depth:rafterDepth},
      affectedRafterIds:affected.map(function(q){return q.id}),
      supportRafterIds:lower&&upper?[lower.id,upper.id]:[],
      supportCrossMm:lower&&upper?[lower.crossMm,upper.crossMm]:[],
      supportInnerFacesMm:lower&&upper?[lowerInside,upperInside]:[],
      trimmerLengthMm:trimmerLength,
      trimmers:affected.length&&lower&&upper?[
        {id:'roof.flueTrimmer.downslope',position:'downslope',centreRunMm:(nearDown+farDown)/2,nearFaceRunMm:nearDown,farFaceRunMm:farDown},
        {id:'roof.flueTrimmer.upslope',position:'upslope',centreRunMm:(nearUp+farUp)/2,nearFaceRunMm:nearUp,farFaceRunMm:farUp}
      ]:[],
      shortRafters:shortRafters,
      shortRafterRunFacesMm:{downslope:nearDown,upslope:nearUp},
      cutRafterRunFacesMm:{downslope:farDown,upslope:farUp}
    },
    roofLayerExclusion:{
      shape:'vertical-cylinder',
      radiusMm:R,
      diameterMm:2*R,
      centrePlanMm:{run:rf.flueRunMm,cross:rf.flueCrossMm},
      rule:'Combustible and ordinary roof build-up remains outside the resolved twin-wall combustible-clearance cylinder.'
    },
    metalRoofCoveringExclusion:{
      shape:'vertical-cylinder',
      radiusMm:flue.outerDiameterMm/2+METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
      diameterMm:flue.outerDiameterMm+2*METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
      clearanceFromFlueOuterEdgeMm:METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
      flueOuterDiameterMm:flue.outerDiameterMm,
      centrePlanMm:{run:rf.flueRunMm,cross:rf.flueCrossMm},
      rule:'Profiled metal roof covering stops 20 mm from the outside edge of the insulated flue casing.'
    }
  };
}

function clipPoly(poly,valueFn,threshold,keepLess){
  if(!poly.length)return[];
  var out=[],prev=poly[poly.length-1],pv=valueFn(prev),pin=keepLess?pv<=threshold+EPS:pv>=threshold-EPS;
  poly.forEach(function(cur){
    var cv=valueFn(cur),cin=keepLess?cv<=threshold+EPS:cv>=threshold-EPS;
    if(cin!==pin){
      var den=cv-pv,t=Math.abs(den)<1e-12?0:(threshold-pv)/den;
      out.push({x:prev.x+(cur.x-prev.x)*t,z:prev.z+(cur.z-prev.z)*t});
    }
    if(cin)out.push({x:cur.x,z:cur.z});
    prev=cur;pv=cv;pin=cin;
  });
  return out;
}

function localProfile(sm){
  var solid=sm&&sm.metadata&&sm.metadata.physicalSolidV1,vs=solid&&solid.verticesMm||[];
  if(!vs.length||vs.length%2)return null;
  var N=vs.length/2,poly=vs.slice(0,N).map(function(v){return{x:num(v.x,0),z:num(v.z,0)}});
  return{poly:poly,widthMm:num(sm.section&&sm.section.widthMm,44),faces:solid.faces||null};
}

function profileScalar(sm,rf,p){
  var w=localToWorld(sm.geometry.localFrame,{x:p.x,y:0,z:p.z});
  return rf.upSign*(coord(w,rf.runAxis)-rf.flueRunMm);
}

function makeSolidFromProfile(sm,poly,frame,width,purposeText){
  if(!poly||poly.length<3)return null;
  var verts=[],half=width/2;
  [-half,half].forEach(function(y){poly.forEach(function(p){verts.push({x:p.x,y:y,z:p.z})})});
  var faces=prismFaces(poly.length),worldPts=verts.map(function(v){return localToWorld(frame,v)});
  return{
    localVertices:verts,
    worldVertices:worldPts,
    faces:faces,
    world:aabbFromPoints(worldPts),
    solid:{schema:'ta-physical-solid-v1',units:'mm',verticesMm:verts,faces:faces,purpose:purposeText}
  };
}

function segmentStructural(sm,rf,threshold,keepLess,id,segmentRole,planeNormalWorld){
  var lp=localProfile(sm); if(!lp)return null;
  var fn=function(p){return profileScalar(sm,rf,p)},poly=clipPoly(lp.poly,fn,threshold,keepLess);
  if(poly.length<3)return null;
  var solid=makeSolidFromProfile(sm,poly,sm.geometry.localFrame,lp.widthMm,'flue-trimmed-rafter-'+segmentRole);
  if(!solid)return null;
  var xs=poly.map(function(p){return p.x}),minX=Math.min.apply(Math,xs),maxX=Math.max.apply(Math,xs),L=maxX-minX,
      out=clone(sm),origEnds=clone(sm.geometry||{}),planeWorld=clone(rf.flueCentreMm||{x:0,y:0,z:0});
  out.id=id;
  out.source=out.source||{};out.source.parentBuildingMemberId=sm.id;out.source.heaterRoofAccommodationVersion=VERSION;
  out.geometry=out.geometry||{};
  out.geometry.finishedLengthMm=L;
  out.geometry.localFrame=clone(sm.geometry.localFrame);
  var boundaryRun=rf.flueRunMm+threshold*rf.upSign,
      planePoint=clone(sm.geometry.localFrame.originMm);
  setCoord(planePoint,rf.runAxis,boundaryRun);
  planePoint.y=0;
  var localPoint=worldPointToLocal(sm.geometry.localFrame,planePoint),localNormal=worldDirToLocal(sm.geometry.localFrame,planeNormalWorld);
  var cutEnd={condition:'plane-cut',plane:{pointLocalMm:localPoint,normalLocal:localNormal}};
  if(segmentRole==='downslope'){
    out.geometry.endA=clone(origEnds.endA);out.geometry.endB=cutEnd;
  }else{
    out.geometry.endA=cutEnd;out.geometry.endB=clone(origEnds.endB);
  }
  out.operations=arr(out.operations).filter(function(op){
    var id0=String(op&&op.id||''),p=String(op&&op.parameters&&op.parameters.purpose||'');
    if(segmentRole==='downslope')return id0.indexOf('.ridge')<0&&id0.indexOf('.highEnd')<0&&id0.indexOf('.birdsmouth.high')<0&&p.indexOf('ridge')<0&&p.indexOf('high-roof')<0;
    if(id0.indexOf('.birdsmouth.high')>=0)return true;
    if(id0.indexOf('.birdsmouth.low')>=0)return false;
    if(/\.birdsmouth$/.test(id0))return false;
    return id0.indexOf('.tail')<0&&id0.indexOf('.lowEnd')<0&&p.indexOf('eaves')<0&&p.indexOf('low-roof')<0;
  });
  out.operations.push({
    id:id+'.flueTrimmerPlane',
    type:'plane-cut',
    referenceFrame:'member-local',
    complete:true,
    parameters:{purpose:'flue-trimmer-plane-cut',segmentRole:segmentRole,worldPlane:{axis:rf.runAxis,valueMm:boundaryRun,vertical:true}}
  });
  out.world=solid.world;
  out.metadata=out.metadata||{};
  out.metadata.parentRafterId=sm.id;
  out.metadata.flueTrimmed=true;
  out.metadata.segmentRole=segmentRole;
  out.metadata.physicalSolidV1=solid.solid;
  out.metadata.maximumPhysicalLongitudinalExtentMm=L;
  out.metadata.stockOptimisationLengthMm=L;
  out.metadata.lengthConvention='maximum-physical-longitudinal-extent-to-flue-trimmer-plane';
  out.readiness=out.readiness||{};
  out.readiness.cutListReady=true;out.readiness.fabricationReady=true;
  return{structural:out,worldVertices:solid.worldVertices,faces:solid.faces,finishedLengthMm:L};
}

function legacySegment(original,seg,id,segmentRole){
  var q=clone(original);
  q.id=id;
  q.cutLengthMm=seg.finishedLengthMm;
  q.world=clone(seg.structural.world);
  q.render=q.render||{};
  q.render.primitive='poly-prism';
  q.render.verticesMm=clone(seg.worldVertices);
  q.render.physicalFaces=clone(seg.faces);
  q.render.role='rafter';
  q.flueTrimmed=true;
  q.parentRafterId=original.id;
  q.metadata=q.metadata||{};
  q.metadata.parentRafterId=original.id;
  q.metadata.flueTrimmed=true;
  q.metadata.segmentRole=segmentRole;
  return q;
}

function makeVerticalTrimmer(plan,building,t){
  var rf=plan._rf,R=plan.flue.exclusionRadiusMm,sec=plan.framing.rafterSectionMm,
      lo=plan.framing.supportInnerFacesMm[0],hi=plan.framing.supportInnerFacesMm[1],
      L=hi-lo,run=t.centreRunMm,crossMid=(lo+hi)/2,top=roofTopY(building,rf,run),cy=top-sec.depth/2,
      p=V(0,cy,0);setCoord(p,rf.runAxis,run);setCoord(p,rf.crossAxis,crossMid);
  var sx=rf.crossAxis==='x'?L:sec.width,sz=rf.crossAxis==='z'?L:sec.width;
  if(rf.runAxis==='x')sx=sec.width;
  else sz=sec.width;
  if(rf.crossAxis==='x')sx=L;
  else sz=L;
  var xAxis=axisVec(rf.crossAxis,1),zAxis=upVec(),yAxis=unit(cross(zAxis,xAxis)),
      origin=clone(p);setCoord(origin,rf.crossAxis,lo);
  var frame={originMm:origin,xAxis:xAxis,yAxis:yAxis,zAxis:zAxis},
      localVerts=[
        {x:0,y:-sec.width/2,z:-sec.depth/2},{x:L,y:-sec.width/2,z:-sec.depth/2},{x:0,y:-sec.width/2,z:sec.depth/2},{x:L,y:-sec.width/2,z:sec.depth/2},
        {x:0,y:sec.width/2,z:-sec.depth/2},{x:L,y:sec.width/2,z:-sec.depth/2},{x:0,y:sec.width/2,z:sec.depth/2},{x:L,y:sec.width/2,z:sec.depth/2}
      ],
      faces=[[0,1,3,2],[4,6,7,5],[0,4,5,1],[2,3,7,6],[0,2,6,4],[1,5,7,3]],
      worldVerts=localVerts.map(function(v){return localToWorld(frame,v)}),world=aabbFromPoints(worldVerts),
      ends={endA:{condition:'square',plane:{pointLocalMm:{x:0,y:0,z:0},normalLocal:{x:-1,y:0,z:0}}},endB:{condition:'square',plane:{pointLocalMm:{x:L,y:0,z:0},normalLocal:{x:1,y:0,z:0}}}},
      src={engine:'ThermoAspenHeaterRoofAccommodation',engineVersion:VERSION};

  var sm={
    schemaName:'ThermoAspenManufacturingMember',schemaVersion:1,id:t.id,units:'mm',coordinateSystemId:COORD,
    source:src,
    assembly:{id:'roof.flueOpening',type:'roof-framing',purpose:'rafter',role:'roof-trimmer',wall:null},
    classification:{partFamily:'timber',manufacturingScope:'cut-only'},
    material:{code:'STRUCTURAL-TIMBER',species:null,strengthGrade:'C16',treatment:null},
    materialCode:'STRUCTURAL-TIMBER',strengthGrade:'C16',
    section:{shape:'rectangular',widthMm:sec.width,heightMm:sec.depth,sourceSection:{nominal:Math.round(sec.width)+'x'+Math.round(sec.depth)}},
    sourceSection:{nominal:Math.round(sec.width)+'x'+Math.round(sec.depth)},
    geometry:{finishedLengthMm:L,localFrame:frame,endA:ends.endA,endB:ends.endB},
    operations:[],drawing:{mark:null,drawingRefs:[]},procurement:{enabled:false},
    readiness:{cutListReady:true,fabricationReady:true,cncExportReady:false,issues:[]},
    world:world,
    metadata:{
      displayLabel:t.position==='upslope'?'Flue upslope trimmer':'Flue downslope trimmer',
      structuralRole:'flue-trimmer',position:t.position,verticalInWorld:true,
      twinWallOuterDiameterMm:plan.flue.outerDiameterMm,combustibleClearanceMm:plan.flue.combustibleClearanceMm,
      nearFaceRunMm:t.nearFaceRunMm,farFaceRunMm:t.farFaceRunMm,
      supportedByRafterIds:clone(plan.framing.supportRafterIds),
      physicalSolidV1:{schema:'ta-physical-solid-v1',units:'mm',verticesMm:localVerts,faces:faces,purpose:'vertical-flue-trimmer'}
    }
  };
  var legacy={
    id:t.id,assemblyId:'roof.flueOpening',assemblyType:'roof-structure',purpose:'rafter',materialCode:'STRUCTURAL-TIMBER',
    render:{primitive:'box',role:'roof-trimmer'},world:world,cutLengthMm:L,source:src,
    metadata:{structuralRole:'flue-trimmer',position:t.position,verticalInWorld:true}
  };
  return{structural:sm,legacy:legacy};
}

function shiftedFrame(frame,axis,target){
  var f=clone(frame),delta=target-coord(f.originMm,axis);setCoord(f.originMm,axis,coord(f.originMm,axis)+delta);return f;
}

function makeShortRafter(template,plan,item,index){
  var rf=plan._rf,R=plan.flue.exclusionRadiusMm,lp=localProfile(template);
  if(!lp)return null;
  var fn=function(p){return profileScalar(template,rf,p)},poly=clipPoly(lp.poly,fn,-R,false);
  poly=clipPoly(poly,fn,R,true);
  if(poly.length<3)return null;
  var frame=shiftedFrame(template.geometry.localFrame,rf.crossAxis,item.crossCentreMm),
      solid=makeSolidFromProfile(template,poly,frame,lp.widthMm,'flue-short-rafter'),
      xs=poly.map(function(p){return p.x}),L=Math.max.apply(Math,xs)-Math.min.apply(Math,xs),
      id='roof.flueShortRafter.'+(item.side==='lower-cross'?'A':'B'),
      downRun=rf.flueRunMm-rf.upSign*R,upRun=rf.flueRunMm+rf.upSign*R,
      downPoint=clone(frame.originMm),upPoint=clone(frame.originMm);
  setCoord(downPoint,rf.runAxis,downRun);downPoint.y=0;
  setCoord(upPoint,rf.runAxis,upRun);upPoint.y=0;
  var downNormal=axisVec(rf.runAxis,-rf.upSign),upNormal=axisVec(rf.runAxis,rf.upSign),
      eA={condition:'plane-cut',plane:{pointLocalMm:worldPointToLocal(frame,downPoint),normalLocal:worldDirToLocal(frame,downNormal)}},
      eB={condition:'plane-cut',plane:{pointLocalMm:worldPointToLocal(frame,upPoint),normalLocal:worldDirToLocal(frame,upNormal)}},
      sm=clone(template);
  sm.id=id;
  sm.source=sm.source||{};sm.source.parentBuildingMemberId=template.id;sm.source.heaterRoofAccommodationVersion=VERSION;
  sm.assembly=sm.assembly||{};sm.assembly.id='roof.flueOpening';sm.assembly.purpose='rafter';sm.assembly.role='short-rafter';
  sm.geometry.finishedLengthMm=L;sm.geometry.localFrame=frame;sm.geometry.endA=eA;sm.geometry.endB=eB;
  sm.operations=[
    {id:id+'.downslope',type:'plane-cut',referenceFrame:'member-local',complete:true,parameters:{purpose:'flue-trimmer-plane-cut',position:'downslope',worldPlane:{axis:rf.runAxis,valueMm:downRun,vertical:true}}},
    {id:id+'.upslope',type:'plane-cut',referenceFrame:'member-local',complete:true,parameters:{purpose:'flue-trimmer-plane-cut',position:'upslope',worldPlane:{axis:rf.runAxis,valueMm:upRun,vertical:true}}}
  ];
  sm.world=solid.world;
  sm.metadata=sm.metadata||{};
  sm.metadata.parentRafterTemplateId=template.id;sm.metadata.structuralRole='flue-short-rafter';sm.metadata.shortRafterSide=item.side;
  sm.metadata.physicalSolidV1=solid.solid;sm.metadata.maximumPhysicalLongitudinalExtentMm=L;sm.metadata.stockOptimisationLengthMm=L;
  sm.metadata.lengthConvention='vertical-trimmer-face-to-face';
  sm.readiness=sm.readiness||{};sm.readiness.cutListReady=true;sm.readiness.fabricationReady=true;sm.readiness.issues=[];

  var legacy={
    id:id,assemblyId:'roof.flueOpening',assemblyType:'roof-structure',purpose:'rafter',materialCode:'STRUCTURAL-TIMBER',
    render:{primitive:'poly-prism',role:'short-rafter',verticesMm:clone(solid.worldVertices),physicalFaces:clone(solid.faces)},
    world:clone(solid.world),cutLengthMm:L,source:clone(sm.source),
    metadata:{structuralRole:'flue-short-rafter',shortRafterSide:item.side,parentRafterTemplateId:template.id}
  };
  return{structural:sm,legacy:legacy};
}

function planeFromPoints(pts){
  if(!pts||pts.length<3)return null;
  var a=pts[0],b=pts[1],c=pts[2],u=sub(b,a),v=sub(c,a),nrm=cross(u,v);
  if(Math.abs(nrm.y)<1e-9)return null;
  return{a:a,n:nrm};
}
function yOnPlane(pl,x,z){
  return pl.a.y-(pl.n.x*(x-pl.a.x)+pl.n.z*(z-pl.a.z))/pl.n.y;
}
function worldFromPlan(rf,run,crossv,y){
  var p=V(0,y,0);setCoord(p,rf.runAxis,run);setCoord(p,rf.crossAxis,crossv);return p;
}

function metalRoofSurface(s){
  var p=purpose(s),
      code=String(s&&s.materialCode||''),
      profile=String(s&&s.metadata&&s.metadata.profileId||s&&s.render&&s.render.profileId||'');
  return p==='corrugated-metal-roof'||code==='ROOF-METAL'||profile.indexOf('sinusoidal')>=0;
}
function roofSurfaceExclusion(surface,plan){
  return metalRoofSurface(surface)&&plan&&plan.metalRoofCoveringExclusion
    ?plan.metalRoofCoveringExclusion
    :plan.roofLayerExclusion;
}

function clipRoofSurface(surface,plan,bandCount){
  var rf=plan._rf,exclusion=roofSurfaceExclusion(surface,plan),
      R=num(exclusion&&exclusion.radiusMm,plan.flue.exclusionRadiusMm),
      pts=arr(surface&&surface.render&&surface.render.pointsMm);
  if(pts.length<3)return[surface];
  var pl=planeFromPoints(pts);if(!pl)return[surface];
  var runs=pts.map(function(p){return coord(p,rf.runAxis)}),crosses=pts.map(function(p){return coord(p,rf.crossAxis)}),
      rMin=Math.min.apply(Math,runs),rMax=Math.max.apply(Math,runs),cMin=Math.min.apply(Math,crosses),cMax=Math.max.apply(Math,crosses),
      fr=rf.flueRunMm,fc=rf.flueCrossMm;
  if(fr+R<=rMin+EPS||fr-R>=rMax-EPS||fc+R<=cMin+EPS||fc-R>=cMax-EPS)return[surface];

  var cA=Math.max(cMin,fc-R),cB=Math.min(cMax,fc+R),bounds=[cMin,cA,cB,cMax],N=Math.max(8,num(bandCount,20));
  if(cB>cA+EPS)for(var i=1;i<N;i++)bounds.push(cA+(cB-cA)*i/N);
  bounds=uniqSorted(bounds);
  var patches=[],idx=0;
  function addPatch(c0,c1,u0,u1){
    if(c1-c0<EPS||u1-u0<EPS)return;
    var p0=worldFromPlan(rf,u0,c0,0),p1=worldFromPlan(rf,u0,c1,0),p2=worldFromPlan(rf,u1,c1,0),p3=worldFromPlan(rf,u1,c0,0);
    [p0,p1,p2,p3].forEach(function(p){p.y=yOnPlane(pl,p.x,p.z)});
    var q=clone(surface);q.id=surface.id+'.flueClip.'+String(++idx).padStart(2,'0');
    q.render=q.render||{};q.render.pointsMm=[p0,p1,p2,p3];q.render.flueClipped=true;
    q.world=aabbFromPoints([p0,p1,p2,p3]);
    q.parentSurfaceId=surface.id;q.fluePenetrationExclusion=clone(exclusion);
    q.metadata=q.metadata||{};q.metadata.parentSurfaceId=surface.id;q.metadata.flueClipped=true;q.metadata.fluePenetrationExclusion=clone(exclusion);
    if(metalRoofSurface(surface)){
      q.metadata.metalRoofClearanceFromFlueEdgeMm=METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM;
      q.metadata.metalRoofCutoutRadiusMm=R;
      q.metadata.metalRoofCutoutDiameterMm=2*R;
    }
    patches.push(q);
  }
  for(var j=0;j<bounds.length-1;j++){
    var c0=bounds[j],c1=bounds[j+1],dmin;
    if(c0<=fc&&c1>=fc)dmin=0;else dmin=Math.min(Math.abs(c0-fc),Math.abs(c1-fc));
    if(dmin>=R-EPS){addPatch(c0,c1,rMin,rMax);continue}
    var half=Math.sqrt(Math.max(0,R*R-dmin*dmin)),lo=fr-half,hi=fr+half;
    addPatch(c0,c1,rMin,Math.min(lo,rMax));
    addPatch(c0,c1,Math.max(hi,rMin),rMax);
  }
  return patches.length?patches:[];
}

function roofLayerSurface(s){
  var p=purpose(s),r=String(s&&s.render&&s.render.role||'');
  return p==='breather-membrane'||p==='corrugated-metal-roof'||p==='roof-finish'||r==='roof-finish'||r==='roof-membrane';
}
function alongRunMember(m){var p=purpose(m);return p==='rafter-batten'}
function acrossRunMember(m){var p=purpose(m);return p==='larch-roof-deck-board'||p==='roof-counter-batten'||p==='counter-batten'}

function segmentBoxMember(m,plan,mode){
  var rf=plan._rf,R=plan.flue.exclusionRadiusMm,w=m&&m.world;
  if(!w||!w.centerMm||!w.sizeMm)return[m];
  var c=clone(w.centerMm),runC=coord(c,rf.runAxis),crossC=coord(c,rf.crossAxis),cos=Math.cos(rf.pitchRad),tan=Math.tan(rf.pitchRad),
      runSize=rf.runAxis==='x'?num(w.sizeMm.x,0):num(w.sizeMm.z,0),
      crossSize=rf.crossAxis==='x'?num(w.sizeMm.x,0):num(w.sizeMm.z,0),
      out=[];
  function make(idSuffix,a,b,axis){
    if(b-a<EPS)return;
    var q=clone(m),nc=clone(c),newCentre=(a+b)/2,L;
    if(axis==='run'){
      var oldRun=runC;setCoord(nc,rf.runAxis,newCentre);nc.y=c.y+(newCentre-oldRun)*rf.upSign*tan;
      L=(b-a)/Math.max(cos,1e-9);
      if(rf.runAxis==='x')q.world.sizeMm.x=L;else q.world.sizeMm.z=L;
      setCoord(q.world.centerMm,rf.runAxis,newCentre);q.world.centerMm.y=nc.y;
      q.cutLengthMm=L;refreshBoxAabb(q);
    }else{
      L=b-a;setCoord(q.world.centerMm,rf.crossAxis,newCentre);
      if(rf.crossAxis==='x')q.world.sizeMm.x=L;else q.world.sizeMm.z=L;
      q.cutLengthMm=L;refreshBoxAabb(q);
    }
    q.id=m.id+'.flueClip.'+idSuffix;q.parentMemberId=m.id;q.fluePenetrationExclusion=clone(plan.roofLayerExclusion);
    q.metadata=q.metadata||{};q.metadata.parentMemberId=m.id;q.metadata.flueClipped=true;q.metadata.fluePenetrationExclusion=clone(plan.roofLayerExclusion);
    out.push(q);
  }

  if(mode==='run'){
    var halfCross=crossSize/2,near=Math.max(0,Math.abs(crossC-rf.flueCrossMm)-halfCross);
    if(near>=R-EPS)return[m];
    var half=Math.sqrt(Math.max(0,R*R-near*near)),
        planHalf=runSize*Math.max(cos,1e-9)/2,
        a=runC-planHalf,b=runC+planHalf,lo=rf.flueRunMm-half,hi=rf.flueRunMm+half;
    if(hi<=a+EPS||lo>=b-EPS)return[m];
    make('A',a,Math.min(lo,b),'run');make('B',Math.max(hi,a),b,'run');
  }else{
    var aabb=w.aabbMm||{},runHalf=(rf.runAxis==='x'?num(aabb.x2,runC)-num(aabb.x1,runC):num(aabb.z2,runC)-num(aabb.z1,runC))/2;
    if(!isFinite(runHalf)||runHalf<=0)runHalf=runSize*Math.max(cos,1e-9)/2;
    var nearRun=Math.max(0,Math.abs(runC-rf.flueRunMm)-runHalf);
    if(nearRun>=R-EPS)return[m];
    var halfC=Math.sqrt(Math.max(0,R*R-nearRun*nearRun)),
        a=crossC-crossSize/2,b=crossC+crossSize/2,lo=rf.flueCrossMm-halfC,hi=rf.flueCrossMm+halfC;
    if(hi<=a+EPS||lo>=b-EPS)return[m];
    make('A',a,Math.min(lo,b),'cross');make('B',Math.max(hi,a),b,'cross');
  }
  return out;
}

function apply(building,installation,options){
  options=options||{};
  var plan=buildPlan(building,installation),errors=clone(plan.errors||[]),warnings=clone(plan.warnings||[]);
  if(!plan.valid)return{valid:false,version:VERSION,plan:plan,building:null,errors:errors,warnings:warnings};
  var out=clone(building),rf=roofFrame(out,flueInfo(installation));plan._rf=rf;rf.flueRunMm=plan.roofLayerExclusion.centrePlanMm.run;rf.flueCrossMm=plan.roofLayerExclusion.centrePlanMm.cross;
  var affectedIds=plan.framing.affectedRafterIds,smById={},lmById={};
  arr(out.structuralMembers).forEach(function(m){smById[m.id]=m});
  arr(out.members).forEach(function(m){lmById[m.id]=m});

  if(affectedIds.length){
    var newSM=[],newLM=[],affectedTemplate=null;
    arr(out.structuralMembers).forEach(function(sm){
      if(affectedIds.indexOf(sm.id)<0){newSM.push(sm);return}
      if(!affectedTemplate)affectedTemplate=clone(sm);
      var down=segmentStructural(sm,rf,-(plan.flue.exclusionRadiusMm+plan.framing.rafterSectionMm.width),true,sm.id+'.downslope','downslope',axisVec(rf.runAxis,rf.upSign)),
          up=segmentStructural(sm,rf,+(plan.flue.exclusionRadiusMm+plan.framing.rafterSectionMm.width),false,sm.id+'.upslope','upslope',axisVec(rf.runAxis,-rf.upSign));
      if(down)newSM.push(down.structural); else issue(warnings,'RAFTER_SEGMENT_OMITTED','roof.rafters.'+sm.id+'.downslope','Downslope rafter segment had no usable geometry.');
      if(up)newSM.push(up.structural); else issue(warnings,'RAFTER_SEGMENT_OMITTED','roof.rafters.'+sm.id+'.upslope','Upslope rafter segment had no usable geometry.');
      var legacy=lmById[sm.id];
      if(legacy){
        if(down)newLM.push(legacySegment(legacy,down,down.structural.id,'downslope'));
        if(up)newLM.push(legacySegment(legacy,up,up.structural.id,'upslope'));
      }
    });
    arr(out.members).forEach(function(m){if(affectedIds.indexOf(m.id)<0)newLM.push(m)});

    plan.framing.trimmers.forEach(function(t){
      var q=makeVerticalTrimmer(plan,out,t);newSM.push(q.structural);newLM.push(q.legacy);
    });
    if(affectedTemplate){
      plan.framing.shortRafters.forEach(function(item,i){
        var q=makeShortRafter(affectedTemplate,plan,item,i);
        if(q){newSM.push(q.structural);newLM.push(q.legacy)}
        else issue(warnings,'SHORT_RAFTER_GEOMETRY_FAILED','roof.shortRafters.'+item.side,'Short-rafter geometry could not be generated from the canonical rafter profile.');
      });
    }
    out.structuralMembers=newSM;
    out.members=newLM;
  }

  /* Clip current linear roof layers that are emitted directly by Building Geometry. */
  var adaptedMembers=[];
  arr(out.members).forEach(function(m){
    if(alongRunMember(m))adaptedMembers=adaptedMembers.concat(segmentBoxMember(m,plan,'run'));
    else if(acrossRunMember(m))adaptedMembers=adaptedMembers.concat(segmentBoxMember(m,plan,'cross'));
    else adaptedMembers.push(m);
  });
  out.members=adaptedMembers;

  /* Clip planar membrane/roof-covering source surfaces around the exclusion cylinder. */
  var adaptedSurfaces=[];
  arr(out.surfaces).forEach(function(s){
    if(roofLayerSurface(s))adaptedSurfaces=adaptedSurfaces.concat(clipRoofSurface(s,plan,options.surfaceBandCount||24));
    else adaptedSurfaces.push(s);
  });
  out.surfaces=adaptedSurfaces;

  out.roofPenetrationAccommodation={
    version:VERSION,
    coordinateSystemId:COORD,
    source:'resolved-heater-installation',
    flue:clone(plan.flue),
    framing:clone(plan.framing),
    roofLayerExclusion:clone(plan.roofLayerExclusion),
    metalRoofCoveringExclusion:clone(plan.metalRoofCoveringExclusion),
    rules:{
      affectedRafterTest:'physical rafter width entering vertical twin-wall exclusion cylinder',
      cutRafters:'vertical plane cuts at outside faces of vertical trimmers',
      trimmers:'two vertical same-section rafter trimmers; nearest faces tangent to exclusion cylinder in the roof-slope plan direction',
      shortRafters:'up to two same-section rafter pieces between trimmers; nearest side face tangent to exclusion cylinder; omit individually if insufficient space',
      roofLayers:'combustible and ordinary roof build-up remains outside the resolved combustible-clearance cylinder',
      metalRoofCovering:'profiled metal covering is cut only 20 mm clear of the insulated flue outer casing; for a 220 mm casing this is a 130 mm radius / 260 mm diameter opening'
    },
    warnings:clone(warnings)
  };
  out.componentCount=arr(out.members).length+arr(out.surfaces).length;
  plan._rf=undefined;
  return{valid:errors.length===0,version:VERSION,plan:plan,building:out,errors:errors,warnings:warnings};
}


/* --------------------------------------------------------------------------
   Ceiling-timber accommodation.
   Ceiling joists remain authoritative output of ceiling_timbers_v1.
   This transformer consumes that completed layer and only adapts members
   intersected by the resolved vertical twin-wall exclusion cylinder.
   -------------------------------------------------------------------------- */

function ceilingJoists(layer){
  return arr(layer&&layer.members).filter(function(m){return purpose(m)==='ceiling-joist'});
}

function dominantHorizontalAxis(v){
  if(!v)return null;
  return Math.abs(num(v.x,0))>=Math.abs(num(v.z,0))?'x':'z';
}

function ceilingFrameInfo(layer,installation){
  var joists=ceilingJoists(layer),flue=flueInfo(installation);
  if(!joists.length||!flue)return null;
  var fr=joists[0]&&joists[0].geometry&&joists[0].geometry.localFrame;
  if(!fr)return null;
  var spanAxis=dominantHorizontalAxis(fr.xAxis),crossAxis=dominantHorizontalAxis(fr.yAxis);
  if(!spanAxis||!crossAxis||spanAxis===crossAxis)crossAxis=otherAxis(spanAxis);
  return{
    spanAxis:spanAxis,
    crossAxis:crossAxis,
    flueSpanMm:coord(flue.centreMm,spanAxis),
    flueCrossMm:coord(flue.centreMm,crossAxis),
    flue:flue
  };
}

function memberCrossDatum(m,cf){
  var fr=m&&m.geometry&&m.geometry.localFrame,w=num(m&&m.section&&m.section.widthMm,44);
  if(!fr)return null;
  return{id:m.id,member:m,crossMm:coord(fr.originMm,cf.crossAxis),widthMm:w};
}

function worldSpanRange(m,cf){
  var a=m&&m.world&&m.world.aabbMm;
  if(a){
    return cf.spanAxis==='x'?[num(a.x1),num(a.x2)]:[num(a.z1),num(a.z2)];
  }
  var fr=m&&m.geometry&&m.geometry.localFrame,L=num(m&&m.geometry&&m.geometry.finishedLengthMm,0);
  if(!fr||L<=0)return null;
  var p0=fr.originMm,p1=localToWorld(fr,{x:L,y:0,z:0});
  var a0=coord(p0,cf.spanAxis),a1=coord(p1,cf.spanAxis);
  return[Math.min(a0,a1),Math.max(a0,a1)];
}

function ceilingPlan(layer,installation){
  var errors=[],warnings=[],cf=ceilingFrameInfo(layer,installation);
  if(!layer||layer.valid===false)issue(errors,'CEILING_LAYER_INVALID','ceilingLayer','A valid ceiling_timbers_v1 layer is required.');
  if(!cf)issue(errors,'CEILING_FRAME_UNAVAILABLE','ceilingLayer','Ceiling joists and a resolved twin-wall flue are required.');
  if(errors.length)return{valid:false,version:VERSION,errors:errors,warnings:warnings};

  var R=cf.flue.exclusionRadiusMm,
      datums=ceilingJoists(layer).map(function(m){return memberCrossDatum(m,cf)}).filter(Boolean).sort(function(a,b){return a.crossMm-b.crossMm}),
      candidates=datums.filter(function(q){
        var range=worldSpanRange(q.member,cf);
        if(!range||cf.flueSpanMm<range[0]-R-EPS||cf.flueSpanMm>range[1]+R+EPS)return false;
        return Math.max(0,Math.abs(q.crossMm-cf.flueCrossMm)-q.widthMm/2)<R-EPS;
      }),
      unaffected=datums.filter(function(q){return candidates.indexOf(q)<0}),
      lower=unaffected.filter(function(q){return q.crossMm<cf.flueCrossMm}).pop()||null,
      upper=unaffected.filter(function(q){return q.crossMm>cf.flueCrossMm})[0]||null;

  if(!candidates.length)
    issue(warnings,'NO_CEILING_JOIST_INTERSECTION','ceiling.joists','No ceiling joist enters the twin-wall exclusion cylinder.');

  /* Opening at the building edge: the exclusion cylinder reaches the outermost ceiling joist on one side, so there is
     no unaffected joist there. That outermost joist is cut flush with the trimmers like every other affected joist, and
     the trimmers run through to its outer face (the wall-plate bearing the joist itself had). One side must still be a
     true unaffected joist; if neither side is, the opening is not supportable and the error stands. */
  var edgeLower=null,edgeUpper=null;
  if(candidates.length&&(!lower||!upper)){
    if(!upper&&lower&&datums[datums.length-1]&&candidates.indexOf(datums[datums.length-1])>=0)edgeUpper=datums[datums.length-1];
    if(!lower&&upper&&datums[0]&&candidates.indexOf(datums[0])>=0)edgeLower=datums[0];
  }
  if(candidates.length&&((!lower&&!edgeLower)||(!upper&&!edgeUpper)||(!lower&&!upper)))
    issue(errors,'CEILING_TRIMMER_SUPPORT_MISSING','ceiling.joists','The flue opening has no unaffected ceiling joist on at least one side and is not at the edge of the ceiling frame.',
      {lowerSupport:lower&&lower.id||null,upperSupport:upper&&upper.id||null});

  var ref=candidates[0]||datums[0],
      width=num(ref&&ref.widthMm,44),
      depth=num(ref&&ref.member&&ref.member.section&&ref.member.section.heightMm,145),
      lowerInside=lower?lower.crossMm+lower.widthMm/2:edgeLower?edgeLower.crossMm-edgeLower.widthMm/2:null,
      upperInside=upper?upper.crossMm-upper.widthMm/2:edgeUpper?edgeUpper.crossMm+edgeUpper.widthMm/2:null,
      hasLower=!!(lower||edgeLower),hasUpper=!!(upper||edgeUpper),
      spanMinusNear=cf.flueSpanMm-R,
      spanPlusNear=cf.flueSpanMm+R,
      spanMinusFar=cf.flueSpanMm-R-width,
      spanPlusFar=cf.flueSpanMm+R+width,
      shortJoists=[];

  if(candidates.length&&hasLower&&hasUpper){
    var left={a:cf.flueCrossMm-R-width,b:cf.flueCrossMm-R},
        right={a:cf.flueCrossMm+R,b:cf.flueCrossMm+R+width};
    if(edgeLower){/* no short joist beyond the edge of the frame */}
    else if(left.a>=lowerInside-EPS)
      shortJoists.push({side:'lower-cross',crossCentreMm:(left.a+left.b)/2,cross0Mm:left.a,cross1Mm:left.b});
    else issue(warnings,'CEILING_SHORT_JOIST_OMITTED','ceiling.shortJoists.lower-cross','Insufficient space for the lower-cross short ceiling joist.',
      {availableMm:(cf.flueCrossMm-R)-lowerInside,requiredMm:width});
    if(edgeUpper){/* no short joist beyond the edge of the frame */}
    else if(right.b<=upperInside+EPS)
      shortJoists.push({side:'upper-cross',crossCentreMm:(right.a+right.b)/2,cross0Mm:right.a,cross1Mm:right.b});
    else issue(warnings,'CEILING_SHORT_JOIST_OMITTED','ceiling.shortJoists.upper-cross','Insufficient space for the upper-cross short ceiling joist.',
      {availableMm:upperInside-(cf.flueCrossMm+R),requiredMm:width});
  }

  return{
    valid:errors.length===0,
    version:VERSION,
    coordinateSystemId:COORD,
    errors:errors,
    warnings:warnings,
    flue:{
      centreMm:clone(cf.flue.centreMm),
      outerDiameterMm:cf.flue.outerDiameterMm,
      combustibleClearanceMm:cf.flue.combustibleClearanceMm,
      exclusionRadiusMm:R,
      exclusionDiameterMm:2*R
    },
    axes:{
      spanAxis:cf.spanAxis,
      crossAxis:cf.crossAxis,
      flueSpanMm:cf.flueSpanMm,
      flueCrossMm:cf.flueCrossMm
    },
    framing:{
      joistSectionMm:{width:width,depth:depth},
      affectedJoistIds:candidates.map(function(q){return q.id}),
      supportJoistIds:[lower&&lower.id,upper&&upper.id].filter(Boolean),
      supportCrossMm:hasLower&&hasUpper?[lower?lower.crossMm:null,upper?upper.crossMm:null]:[],
      supportInnerFacesMm:hasLower&&hasUpper?[lowerInside,upperInside]:[],
      edgeOpening:{lower:!!edgeLower,upper:!!edgeUpper,edgeJoistIds:[edgeLower&&edgeLower.id,edgeUpper&&edgeUpper.id].filter(Boolean),
        bearing:(edgeLower||edgeUpper)?'trimmers run through to the outer face of the cut edge joist (wall-plate bearing); edge joist cut flush with the trimmers':null},
      trimmerLengthMm:hasLower&&hasUpper?upperInside-lowerInside:null,
      trimmers:candidates.length&&hasLower&&hasUpper?[
        {id:'ceiling.flueTrimmer.spanMinus',position:'span-minus',centreSpanMm:(spanMinusNear+spanMinusFar)/2,nearFaceSpanMm:spanMinusNear,farFaceSpanMm:spanMinusFar},
        {id:'ceiling.flueTrimmer.spanPlus',position:'span-plus',centreSpanMm:(spanPlusNear+spanPlusFar)/2,nearFaceSpanMm:spanPlusNear,farFaceSpanMm:spanPlusFar}
      ]:[],
      shortJoists:shortJoists,
      shortJoistEndFacesMm:{spanMinus:spanMinusNear,spanPlus:spanPlusNear},
      cutJoistEndFacesMm:{spanMinus:spanMinusFar,spanPlus:spanPlusFar}
    }
  };
}

function ceilingSegment(sm,cf,threshold,keepLess,id,segmentRole){
  var lp=localProfile(sm);
  if(!lp)return null;
  var scalar=function(p){
    var w=localToWorld(sm.geometry.localFrame,{x:p.x,y:0,z:p.z});
    return coord(w,cf.spanAxis)-cf.flueSpanMm;
  };
  var poly=clipPoly(lp.poly,scalar,threshold,keepLess);
  if(poly.length<3)return null;
  var solid=makeSolidFromProfile(sm,poly,sm.geometry.localFrame,lp.widthMm,'flue-trimmed-ceiling-joist-'+segmentRole);
  if(!solid)return null;

  var xs=poly.map(function(p){return p.x}),L=Math.max.apply(Math,xs)-Math.min.apply(Math,xs),
      out=clone(sm),boundarySpan=cf.flueSpanMm+threshold,
      planePoint=clone(sm.geometry.localFrame.originMm);
  setCoord(planePoint,cf.spanAxis,boundarySpan);
  var normalWorld=axisVec(cf.spanAxis,segmentRole==='span-minus'?1:-1),
      localPoint=worldPointToLocal(sm.geometry.localFrame,planePoint),
      localNormal=worldDirToLocal(sm.geometry.localFrame,normalWorld),
      cut={condition:'plane-cut',plane:{pointLocalMm:localPoint,normalLocal:localNormal}};

  out.id=id;
  out.source=out.source||{};
  out.source.parentCeilingJoistId=sm.id;
  out.source.heaterRoofAccommodationVersion=VERSION;
  out.geometry=out.geometry||{};
  out.geometry.finishedLengthMm=L;
  if(segmentRole==='span-minus'){
    out.geometry.endA=clone(sm.geometry.endA);
    out.geometry.endB=cut;
  }else{
    out.geometry.endA=cut;
    out.geometry.endB=clone(sm.geometry.endB);
  }

  out.operations=arr(out.operations).filter(function(op){
    var p=String(op&&op.parameters&&op.parameters.purpose||'');
    return p.indexOf('flue-trimmer')<0;
  });
  out.operations.push({
    id:id+'.flueTrimmerCut',
    type:'plane-cut',
    referenceFrame:'member-local',
    complete:true,
    parameters:{
      purpose:'ceiling-flue-trimmer-plane-cut',
      segmentRole:segmentRole,
      squareToMemberAxis:true,
      worldPlane:{axis:cf.spanAxis,valueMm:boundarySpan,vertical:true}
    }
  });
  out.world=solid.world;
  out.metadata=out.metadata||{};
  out.metadata.parentCeilingJoistId=sm.id;
  out.metadata.flueTrimmedCeilingJoist=true;
  out.metadata.segmentRole=segmentRole;
  out.metadata.physicalSolidV1=solid.solid;
  out.metadata.maximumPhysicalLongitudinalExtentMm=L;
  out.metadata.stockOptimisationLengthMm=L;
  out.metadata.lengthConvention='bearing-end-to-flue-trimmer-plane';
  out.readiness=out.readiness||{};
  out.readiness.cutListReady=true;
  return out;
}

function makeCeilingTrimmer(plan,template,t){
  var cf=plan._cf,sec=plan.framing.joistSectionMm,
      lo=plan.framing.supportInnerFacesMm[0],hi=plan.framing.supportInnerFacesMm[1],
      L=hi-lo,fr0=template.geometry.localFrame,
      y=num(fr0.originMm.y,0),
      origin=V(0,y,0);
  setCoord(origin,cf.spanAxis,t.centreSpanMm);
  setCoord(origin,cf.crossAxis,lo);

  var xAxis=axisVec(cf.crossAxis,1),zAxis=upVec(),yAxis=unit(cross(zAxis,xAxis)),
      frame={originMm:origin,xAxis:xAxis,yAxis:yAxis,zAxis:zAxis},
      w=sec.width,d=sec.depth,
      verts=[
        {x:0,y:-w/2,z:-d/2},{x:L,y:-w/2,z:-d/2},{x:0,y:-w/2,z:d/2},{x:L,y:-w/2,z:d/2},
        {x:0,y:w/2,z:-d/2},{x:L,y:w/2,z:-d/2},{x:0,y:w/2,z:d/2},{x:L,y:w/2,z:d/2}
      ],
      faces=[[0,1,3,2],[4,6,7,5],[0,4,5,1],[2,3,7,6],[0,2,6,4],[1,5,7,3]],
      worldPts=verts.map(function(v){return localToWorld(frame,v)}),
      ends={endA:{condition:'square',plane:{pointLocalMm:{x:0,y:0,z:0},normalLocal:{x:-1,y:0,z:0}}},
            endB:{condition:'square',plane:{pointLocalMm:{x:L,y:0,z:0},normalLocal:{x:1,y:0,z:0}}}},
      m=clone(template);

  m.id=t.id;
  m.source=m.source||{};
  m.source.parentCeilingJoistId=null;
  m.source.heaterRoofAccommodationVersion=VERSION;
  m.assembly={id:'ceiling.flueOpening',type:'ceiling-framing',purpose:'ceiling-joist',role:'ceiling-trimmer',wall:null};
  m.classification=m.classification||{partFamily:'timber',manufacturingScope:'cut-only'};
  m.classification.manufacturingScope='cut-only';
  m.geometry={finishedLengthMm:L,localFrame:frame,endA:ends.endA,endB:ends.endB};
  m.operations=[];
  m.world=aabbFromPoints(worldPts);
  m.metadata=clone(m.metadata||{});
  m.metadata.displayLabel=t.position==='span-minus'?'Flue ceiling trimmer A':'Flue ceiling trimmer B';
  m.metadata.structuralRole='ceiling-flue-trimmer';
  m.metadata.position=t.position;
  m.metadata.horizontalInWorld=true;
  m.metadata.supportedByCeilingJoistIds=clone(plan.framing.supportJoistIds);
  m.metadata.edgeBearing=clone(plan.framing.edgeOpening);
  m.metadata.twinWallOuterDiameterMm=plan.flue.outerDiameterMm;
  m.metadata.combustibleClearanceMm=plan.flue.combustibleClearanceMm;
  m.metadata.nearFaceSpanMm=t.nearFaceSpanMm;
  m.metadata.farFaceSpanMm=t.farFaceSpanMm;
  m.metadata.physicalSolidV1={schema:'ta-physical-solid-v1',units:'mm',verticesMm:verts,faces:faces,purpose:'ceiling-flue-trimmer'};
  m.metadata.maximumPhysicalLongitudinalExtentMm=L;
  m.metadata.stockOptimisationLengthMm=L;
  m.metadata.lengthConvention=(plan.framing.edgeOpening.lower||plan.framing.edgeOpening.upper)?'support-joist-inner-face-to-edge-joist-outer-face':'support-joist-inner-face-to-inner-face';
  m.readiness=m.readiness||{};
  m.readiness.cutListReady=true;
  return m;
}

function makeCeilingShortJoist(plan,template,item){
  var cf=plan._cf,R=plan.flue.exclusionRadiusMm,lp=localProfile(template);
  if(!lp)return null;
  var scalar=function(p){
    var w=localToWorld(template.geometry.localFrame,{x:p.x,y:0,z:p.z});
    return coord(w,cf.spanAxis)-cf.flueSpanMm;
  };
  var poly=clipPoly(lp.poly,scalar,-R,false);
  poly=clipPoly(poly,scalar,R,true);
  if(poly.length<3)return null;

  var frame=shiftedFrame(template.geometry.localFrame,cf.crossAxis,item.crossCentreMm),
      solid=makeSolidFromProfile(template,poly,frame,lp.widthMm,'flue-short-ceiling-joist');
  if(!solid)return null;

  var xs=poly.map(function(p){return p.x}),L=Math.max.apply(Math,xs)-Math.min.apply(Math,xs),
      minusPoint=clone(frame.originMm),plusPoint=clone(frame.originMm);
  setCoord(minusPoint,cf.spanAxis,cf.flueSpanMm-R);
  setCoord(plusPoint,cf.spanAxis,cf.flueSpanMm+R);

  var m=clone(template),id='ceiling.flueShortJoist.'+(item.side==='lower-cross'?'A':'B');
  m.id=id;
  m.source=m.source||{};
  m.source.parentCeilingJoistId=template.id;
  m.source.heaterRoofAccommodationVersion=VERSION;
  m.assembly={id:'ceiling.flueOpening',type:'ceiling-framing',purpose:'ceiling-joist',role:'ceiling-short-joist',wall:null};
  m.geometry.finishedLengthMm=L;
  m.geometry.localFrame=frame;
  m.geometry.endA={condition:'plane-cut',plane:{pointLocalMm:worldPointToLocal(frame,minusPoint),normalLocal:worldDirToLocal(frame,axisVec(cf.spanAxis,-1))}};
  m.geometry.endB={condition:'plane-cut',plane:{pointLocalMm:worldPointToLocal(frame,plusPoint),normalLocal:worldDirToLocal(frame,axisVec(cf.spanAxis,1))}};
  m.operations=[
    {id:id+'.spanMinus',type:'plane-cut',referenceFrame:'member-local',complete:true,parameters:{purpose:'ceiling-flue-trimmer-plane-cut',position:'span-minus',squareToMemberAxis:true,worldPlane:{axis:cf.spanAxis,valueMm:cf.flueSpanMm-R,vertical:true}}},
    {id:id+'.spanPlus',type:'plane-cut',referenceFrame:'member-local',complete:true,parameters:{purpose:'ceiling-flue-trimmer-plane-cut',position:'span-plus',squareToMemberAxis:true,worldPlane:{axis:cf.spanAxis,valueMm:cf.flueSpanMm+R,vertical:true}}}
  ];
  m.world=solid.world;
  m.metadata=clone(m.metadata||{});
  m.metadata.parentCeilingJoistTemplateId=template.id;
  m.metadata.structuralRole='ceiling-flue-short-joist';
  m.metadata.shortJoistSide=item.side;
  m.metadata.physicalSolidV1=solid.solid;
  m.metadata.maximumPhysicalLongitudinalExtentMm=L;
  m.metadata.stockOptimisationLengthMm=L;
  m.metadata.lengthConvention='ceiling-flue-trimmer-face-to-face';
  m.readiness=m.readiness||{};
  m.readiness.cutListReady=true;
  return m;
}

function applyCeilingLayer(layer,installation){
  var plan=ceilingPlan(layer,installation),errors=clone(plan.errors||[]),warnings=clone(plan.warnings||[]);
  if(!plan.valid)return{valid:false,version:VERSION,plan:plan,layer:clone(layer),errors:errors,warnings:warnings};

  var out=clone(layer),cf=ceilingFrameInfo(layer,installation);
  plan._cf=cf;

  var affected=plan.framing.affectedJoistIds,
      template=null,newMembers=[];

  arr(out.members).forEach(function(m){
    if(purpose(m)!=='ceiling-joist'||affected.indexOf(m.id)<0){
      newMembers.push(m);
      return;
    }
    if(!template)template=clone(m);

    var minus=ceilingSegment(
      m,cf,
      -(plan.flue.exclusionRadiusMm+plan.framing.joistSectionMm.width),
      true,
      m.id+'.spanMinus',
      'span-minus'
    );
    var plus=ceilingSegment(
      m,cf,
      +(plan.flue.exclusionRadiusMm+plan.framing.joistSectionMm.width),
      false,
      m.id+'.spanPlus',
      'span-plus'
    );

    if(minus)newMembers.push(minus);
    else issue(warnings,'CEILING_JOIST_SEGMENT_OMITTED','ceiling.'+m.id+'.spanMinus','Span-minus ceiling-joist segment had no usable geometry.');
    if(plus)newMembers.push(plus);
    else issue(warnings,'CEILING_JOIST_SEGMENT_OMITTED','ceiling.'+m.id+'.spanPlus','Span-plus ceiling-joist segment had no usable geometry.');
  });

  if(affected.length&&template){
    plan.framing.trimmers.forEach(function(t){
      newMembers.push(makeCeilingTrimmer(plan,template,t));
    });
    plan.framing.shortJoists.forEach(function(item){
      var q=makeCeilingShortJoist(plan,template,item);
      if(q)newMembers.push(q);
      else issue(warnings,'CEILING_SHORT_JOIST_GEOMETRY_FAILED','ceiling.shortJoists.'+item.side,'Short ceiling-joist geometry could not be generated from the authoritative joist profile.');
    });
  }

  out.members=newMembers;
  out.warnings=arr(out.warnings).concat(warnings.map(function(x){return x.message||String(x)}));
  out.metadata=out.metadata||{};
  out.metadata.heaterFlueAccommodation={
    version:VERSION,
    authority:'post-process of ceiling_timbers_v1 output',
    affectedJoistIds:clone(plan.framing.affectedJoistIds),
    supportJoistIds:clone(plan.framing.supportJoistIds),
    edgeOpening:clone(plan.framing.edgeOpening),
    trimmerIds:plan.framing.trimmers.map(function(x){return x.id}),
    shortJoistCount:plan.framing.shortJoists.length,
    twinWallOuterDiameterMm:plan.flue.outerDiameterMm,
    combustibleClearanceMm:plan.flue.combustibleClearanceMm,
    exclusionDiameterMm:plan.flue.exclusionDiameterMm
  };
  out.metadata.joistCount=newMembers.filter(function(m){return purpose(m)==='ceiling-joist'}).length;

  plan._cf=undefined;
  return{
    valid:errors.length===0,
    version:VERSION,
    plan:plan,
    layer:out,
    errors:errors,
    warnings:warnings
  };
}


function validateEnvironment(){
  return{
    valid:true,
    version:VERSION,
    coordinateSystemId:COORD,
    defaultTwinWallOuterDiameterMm:DEFAULT_TWINWALL_OUTER_MM,
    defaultCombustibleClearanceMm:DEFAULT_COMBUSTIBLE_CLEARANCE_MM,
    defaultExclusionDiameterMm:DEFAULT_TWINWALL_OUTER_MM+2*DEFAULT_COMBUSTIBLE_CLEARANCE_MM,
    metalRoofClearanceFromFlueOuterEdgeMm:METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
    defaultMetalRoofCutoutRadiusMm:DEFAULT_TWINWALL_OUTER_MM/2+METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
    defaultMetalRoofCutoutDiameterMm:DEFAULT_TWINWALL_OUTER_MM+2*METAL_ROOF_CLEARANCE_FROM_FLUE_EDGE_MM,
    errors:[],warnings:[]
  };
}

var API={
  version:VERSION,
  coordinateSystemId:COORD,
  validateEnvironment:validateEnvironment,
  plan:buildPlan,
  apply:apply,

  planCeilingLayer:ceilingPlan,
  applyCeilingLayer:applyCeilingLayer
};

W.ThermoAspenHeaterRoofAccommodationV1=API;
try{W.dispatchEvent(new CustomEvent('ThermoAspenHeaterRoofAccommodationReady',{detail:validateEnvironment()}))}catch(e){}
})(window);
